import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { CheckoutService } from '@b2b/features/cart/core/checkout.service';
import { CheckoutStateService } from '@b2b/features/cart/core/checkout-state.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'checkout-payment',
  imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule, NgxMaskDirective],
  providers: [provideNgxMask()],
  templateUrl: './checkout-payment.component.html',
  styleUrl: './checkout-payment.component.scss',
})
export class CheckoutPaymentComponent implements OnDestroy {
  tabs = this.checkoutService.itemsTabs('pagamento');

  paymentMethod = computed(() => this.checkoutStateService.paymentMethod());

  months: string[] = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  years: string[] = Array.from({ length: 15 }, (_, i) => String(new Date().getFullYear() + i));

  paymentConditionOptions = [
    { value: '30', label: '30 dias' },
    { value: '30_60', label: '30/60 dias' },
    { value: '30_60_90', label: '30/60/90 dias' },
    { value: '60', label: '60 dias' },
  ];

  private formSubscription: Subscription | null = null;

  creditCardForm: FormGroup = this.fb.group({
    paymentCondition: ['', Validators.required],
    cpfCnpj: ['', Validators.required],
    cardHolderName: ['', Validators.required],
    cardNumber: ['', Validators.required],
    expirationMonth: ['', Validators.required],
    expirationYear: ['', Validators.required],
    cvv: ['', Validators.required],
  });

  boletoForm: FormGroup = this.fb.group({
    paymentCondition: ['', Validators.required],
  });

  constructor(
    private router: Router,
    private checkoutService: CheckoutService,
    private checkoutStateService: CheckoutStateService,
    private fb: FormBuilder,
  ) {
    // Reage a mudanças no método de pagamento e mantém o signal paymentFormValid atualizado
    effect(() => {
      const method = this.paymentMethod();
      this.updateFormValidState(method);
    });
  }

  private updateFormValidState(method: string | null): void {
    this.formSubscription?.unsubscribe();
    this.formSubscription = null;

    if (method === 'PIX') {
      this.checkoutStateService.setPaymentFormValid(true);
    } else if (method === 'CREDIT_CARD') {
      this.checkoutStateService.setPaymentFormValid(this.creditCardForm.valid);
      this.formSubscription = this.creditCardForm.statusChanges.subscribe(() => {
        this.checkoutStateService.setPaymentFormValid(this.creditCardForm.valid);
      });
    } else if (method === 'BOLETO') {
      this.checkoutStateService.setPaymentFormValid(this.boletoForm.valid);
      this.formSubscription = this.boletoForm.statusChanges.subscribe(() => {
        this.checkoutStateService.setPaymentFormValid(this.boletoForm.valid);
      });
    } else {
      this.checkoutStateService.setPaymentFormValid(false);
    }
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }

  get isFormValid(): boolean {
    const method = this.paymentMethod();
    if (method === 'CREDIT_CARD') return this.creditCardForm.valid;
    if (method === 'BOLETO') return this.boletoForm.valid;
    if (method === 'PIX') return true;
    return false;
  }

  continue() {
    if (!this.isFormValid) return;
    this.router.navigate(['/checkout/review']);
  }

  back() {
    this.router.navigate(['/checkout/metodo-pagamento']);
  }
}
